import moment from 'moment';
import React from 'react';
import { Table, Tag, Button, Card, Tooltip } from 'antd';
import { EditOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';

function TableSubjectTeachingTeacher(props) {
    const handleEditClick = (scheduleData) => {
        if (props.onEditClick) {
            props.onEditClick(scheduleData);
        }
    };

    const renderScheduleItem = (schedule, sessionColor) => {
        const isExam = schedule.examId || schedule['subjectTeachingName']?.includes('[');

        return (
            <Card
                size="small"
                className={`mb-2 last:mb-0 ${isExam ? 'border-red-300 bg-red-50' : 'border-gray-200'} hover:shadow-md transition-shadow`}
                bordered
                style={{width: '110px'}}
            >
                <div className="flex flex-col gap-2">
                    <div className={`font-medium text-xs ${isExam ? 'text-red-600' : 'text-gray-800'} leading-tight break-words`}>
                        {schedule['subjectTeachingName']}
                    </div>
                    <div className="flex flex-col gap-1">
                        <Tag color="blue" className="text-xs m-0 break-words whitespace-normal">
                            {schedule['roomName']}
                        </Tag>
                        <Tag color={sessionColor} className="text-xs m-0 whitespace-normal">
                            {moment(schedule.startDateTime).format('HH:mm')} - {moment(schedule.endDateTime).format('HH:mm')}
                        </Tag>
                    </div>
                </div>
            </Card>
        );
    };

    const renderSessionCell = (schedules, sessionType, sessionColor, bgColor) => {
        if (!schedules || schedules.length === 0) {
            return <div className="text-center text-gray-400 py-4">-</div>;
        }

        return (
            <div className={`${bgColor} min-h-[60px]`}>
                {schedules.map((schedule, idx) => (
                    <React.Fragment key={`${sessionType}-schedule-${idx}`}>
                        {renderScheduleItem(schedule, sessionColor)}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const columns = [
        {
            title: <div className="text-center font-bold">Ngày</div>,
            dataIndex: 'date',
            key: 'date',
            fixed: 'left',
            width: 80,
            render: (date, record, index) => {
                const dayOfWeek = moment(date).day();
                const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

                // Only render on the first session (morning) of each day, merge 3 rows
                const obj = {
                    children: (
                        <div className="text-center font-semibold text-xs">
                            <div>{dayNames[dayOfWeek]}</div>
                            <div className="text-gray-600">{moment(date).format('DD/MM')}</div>
                        </div>
                    ),
                    props: {},
                };

                // Check if this is the morning session (first session of the day)
                if (record.session === 'S') {
                    obj.props.rowSpan = 3; // Merge 3 rows (S, C, T)
                } else {
                    obj.props.rowSpan = 0; // Hide for afternoon and night
                }

                return obj;
            },
        },
        {
            title: <div className="text-center font-bold">Buổi</div>,
            dataIndex: 'session',
            key: 'session',
            fixed: 'left',
            width: 50,
            render: (session) => {
                const sessionConfig = {
                    'S': { text: 'Sáng', icon: 'S' },
                    'C': { text: 'Chiều', icon: 'C' },
                    'T': { text: 'Tối', icon: 'T' }
                };
                const config = sessionConfig[session] || {};
                return (
                    <Tooltip title={config.text}>
                        <div className="text-center font-semibold">
                            <div>{config.icon}</div>
                        </div>
                    </Tooltip>
                );
            },
        },
    ];

    // Add teacher columns
    if (props.data?.teachers) {
        props.data.teachers.forEach((teacher, teacherIdx) => {
            columns.push({
                title: (
                    <div className="text-center font-bold text-xs">
                        {teacher.teacherName}
                    </div>
                ),
                dataIndex: `teacher_${teacherIdx}`,
                key: `teacher_${teacherIdx}`,
                width: 60,
                render: (_, record) => {
                    const schedules = record[`teacher_${teacherIdx}_schedules`];
                    const sessionColors = {
                        'S': 'blue',
                        'C': 'orange',
                        'T': 'green'
                    };
                    const bgColors = {
                        'S': 'bg-blue-50',
                        'C': 'bg-orange-50',
                        'T': 'bg-green-50'
                    };
                    return renderSessionCell(
                        schedules,
                        record.session,
                        sessionColors[record.session],
                        bgColors[record.session]
                    );
                },
            });
        });
    }

    // Transform data for table
    const tableData = [];
    if (props.data?.times) {
        props.data.times.forEach((time, timeIdx) => {
            const currentDate = moment(time).format('YYYY-MM-DD');

            // Morning
            const morningRow = {
                key: `${timeIdx}-morning`,
                date: time,
                session: 'S',
            };
            props.data.teachers?.forEach((teacher, teacherIdx) => {
                // Filter morning schedules for this specific date
                const morningSchedules = (teacher.season.morning || []).filter(schedule => {
                    const scheduleDate = moment(schedule.startDateTime).format('YYYY-MM-DD');
                    const scheduleHour = moment(schedule.startDateTime).hour();
                    // Morning is typically 6:00 - 11:59
                    return scheduleDate === currentDate && scheduleHour >= 6 && scheduleHour < 12;
                });
                morningRow[`teacher_${teacherIdx}_schedules`] = morningSchedules;
            });
            tableData.push(morningRow);

            // Afternoon
            const afternoonRow = {
                key: `${timeIdx}-afternoon`,
                date: time,
                session: 'C',
            };
            props.data.teachers?.forEach((teacher, teacherIdx) => {
                // Filter afternoon schedules for this specific date
                const afternoonSchedules = (teacher.season.afternoon || []).filter(schedule => {
                    const scheduleDate = moment(schedule.startDateTime).format('YYYY-MM-DD');
                    const scheduleHour = moment(schedule.startDateTime).hour();
                    // Afternoon is typically 12:00 - 16:59
                    return scheduleDate === currentDate && scheduleHour >= 12 && scheduleHour < 17;
                });
                afternoonRow[`teacher_${teacherIdx}_schedules`] = afternoonSchedules;
            });
            tableData.push(afternoonRow);

            // Night
            const nightRow = {
                key: `${timeIdx}-night`,
                date: time,
                session: 'T',
            };
            props.data.teachers?.forEach((teacher, teacherIdx) => {
                // Filter night schedules for this specific date
                const nightSchedules = (teacher.season.night || []).filter(schedule => {
                    const scheduleDate = moment(schedule.startDateTime).format('YYYY-MM-DD');
                    const scheduleHour = moment(schedule.startDateTime).hour();
                    // Night is typically 17:00 - 23:59
                    return scheduleDate === currentDate && scheduleHour >= 17;
                });
                nightRow[`teacher_${teacherIdx}_schedules`] = nightSchedules;
            });
            tableData.push(nightRow);
        });
    }

    return (
        <div className="">
            {props.data != null && (props.data.teachers?.length > 0 || props.data.times?.length > 0) ? (
                <Card
                    title={
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-gray-800 m-0">
                                👨‍🏫 DANH SÁCH LỊCH HỌC THEO GIÁO VIÊN
                            </h3>
                        </div>
                    }
                    className="shadow-lg"
                >
                    <Table
                        columns={columns}
                        dataSource={tableData}
                        pagination={false}
                        scroll={{ x: 'max-content', y: 550 }}
                        size="small"
                        bordered
                        sticky
                        rowClassName={(record, index) => {
                            const colors = {
                                'S': 'bg-blue-50',
                                'C': 'bg-yellow-50',
                                'T': 'bg-green-100'
                            };
                            return colors[record.session] || '';
                        }}
                        className="custom-schedule-table"
                    />
                </Card>
            ) : (
                <Card className="shadow-md">
                    <div className="text-center text-gray-500 py-8">
                        <div className="text-4xl mb-4">📅</div>
                        <div className="text-lg font-medium">Không có dữ liệu lịch học!</div>
                    </div>
                </Card>
            )}
        </div>
    );
}

export default TableSubjectTeachingTeacher;
